import { ContentType } from "@brookbush/database";
import type { CourseSection } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { ChevronsDownIcon, ChevronsUp, ChevronsUpIcon } from "lucide-react";
import { memo, useState } from "react";
import { flushSync } from "react-dom";
import { FaCheckCircle } from "react-icons/fa";

import type { CourseSectionHeaderProps } from "./CourseSectionHeader";
import Abstract from "~/components/Abstract";
import { AnnotationsProvider } from "~/components/Annotations";
import TextBlock from "~/components/TextBlock";
import { useCourseProgress } from "~/hooks";
import { scrollToElement } from "~/utils/helpers";

interface CourseSectionBodyProps extends CourseSectionHeaderProps {
	toggleIsExpanded: (section: CourseSection) => void;
}
const CourseSectionBody = ({
	section,
	isCompleted,
	course,
	toggleIsExpanded,
	isFreeSection,
	isFirstSection,
	focusSections,
}: CourseSectionBodyProps) => {
	const subSections = section.subSections || [];
	const [isSectionIntroExpanded, setIsSectionIntroExpanded] = useState(false);
	const { addCourseProgress, isLoadingCourseProgress } =
		useCourseProgress(course);

	const { isSignedIn } = useAuth();

	const focusSectionData = focusSections?.find((s) => {
		if (section.parent) {
			return s.subSectionKey === section._key;
		}
		return s.sectionKey === section._key;
	});

	const isFocusSection = !!focusSectionData;

	return (
		<div
			className={clsx(
				"relative flex flex-col gap-2 rounded-b-xl border-2 phablet:px-5 px-4 phablet:py-3 py-2",
				isFocusSection
					? "border-purple-500/50 dark:border-purple-400/40"
					: isFreeSection
						? "border-slate-300 dark:border-slate-500"
						: isCompleted
							? "border-emerald-500/50 dark:border-emerald-300/40"
							: section.parent
								? "border-amber-700/20 dark:border-amber-300/40"
								: "border-sky-700/30 dark:border-sky-200/30",
			)}
			key={`${section._key}-content`}
		>
			<AnnotationsProvider
				contentKey={section._key}
				key={section._key}
				highlight
				contentId={course._id}
				contentType={ContentType.COURSE}
				askAI
			>
				{isFirstSection && course.isScholarly && course.abstract ? (
					<Abstract abstract={course.abstract} />
				) : null}
				<TextBlock
					expanded={isSectionIntroExpanded || !subSections.length}
					value={section.body!}
					isBiblio={section.title === "Bibliography"}
				/>
			</AnnotationsProvider>

			<div className="flex items-center justify-center gap-2 pt-2 pb-3">
				{!!subSections.length &&
					(!isSectionIntroExpanded ? (
						<button
							onClick={() => setIsSectionIntroExpanded(true)}
							className="flex items-center gap-0.5 rounded-full border bg-white px-3 py-1.5 hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700"
						>
							Read More
							<ChevronsDownIcon className="-mr-1" size={20} />
						</button>
					) : (
						<button
							onClick={() => {
								flushSync(() => setIsSectionIntroExpanded(false));
								scrollToElement(section._key);
							}}
							className="flex items-center gap-0.5 rounded-full border bg-white px-3 py-1.5 hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700"
						>
							Read Less
							<ChevronsUpIcon className="-mr-1" size={20} />
						</button>
					))}

				{!isCompleted && !isFreeSection && !section.subSections?.length && (
					<button
						onClick={async () => {
							addCourseProgress(section as CourseSection);
							toggleIsExpanded(section);
							scrollToElement(section._key);
						}}
						className="flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700"
						disabled={isLoadingCourseProgress || !isSignedIn}
					>
						Mark As Read{" "}
						<FaCheckCircle className="text-emerald-400" size={17} />
					</button>
				)}

				{!subSections.length && !isSectionIntroExpanded && (
					<button
						onClick={() => {
							toggleIsExpanded(section);
							scrollToElement(section._key);
						}}
						className="flex items-center gap-0.5 rounded-full border bg-white px-3 py-1.5 hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700"
					>
						Collapse
						<ChevronsUp className="-mr-1" size={20} />
					</button>
				)}
			</div>
		</div>
	);
};

export default memo(CourseSectionBody);
